import { Controller } from "@hotwired/stimulus"
import { calculate } from "../modules/salary_lab/calculator_api"
import { formatCurrency, formatPercent, parseNumber } from "../modules/salary_lab/formatter"
import { SmartDebounce, detectEventType } from "../modules/salary_lab/smart_debounce"
import { writeGenericToUrl, readGenericFromUrl } from "../modules/salary_lab/url_state"
import { trackCalculatorUsed } from "../modules/salary_lab/ga4_tracker"

const URL_MAP = { rate: "rate", rate_type: "rate_type", working_weeks: "weeks", hours_per_week: "hrs", days_per_week: "days", gst_registered: "gst", expense_percent: "exp", kiwisaver_perc: "ks", include_student_loan: "sl" }
const URL_TYPES = { rate: "number", working_weeks: "number", hours_per_week: "number", days_per_week: "number", gst_registered: "boolean", expense_percent: "number", kiwisaver_perc: "number", include_student_loan: "boolean" }

export default class extends Controller {
  // Values are wired from the view using server-side constants —
  // see Custom::SalaryLab::CalculatorsController#set_salary_lab_constants. No hardcoded
  // defaults here; see comment in salary_lab_comparison_controller.js
  // for the rationale.
  static values = {
    defaultWeeks: Number,
    defaultHours: Number,
    defaultDays: Number,
    defaultExpensePercent: Number
  }

  static targets = [
    "rateInput", "rateTypeSelect", "workingWeeksInput",
    "hoursInput", "daysInput", "hoursField", "daysField",
    "gstCheckbox", "expenseInput", "expenseDisplay",
    "kiwisaverSelect", "studentLoanCheckbox",
    "resultsPanel", "loadingIndicator",
    "netTakeHome", "effectiveHourly", "effectiveDailyRate",
    "grossIncome", "gstAmount", "gstInclusiveIncome",
    "businessExpenses", "taxableIncome",
    "payeTax", "accLevy", "kiwisaverVoluntary",
    "equivalentPaye", "noLeaveCost",
    "provisionalTax", "gstQuarterly",
    "annualNet", "monthlyNet", "fortnightlyNet", "weeklyNet",
    "gstSection", "provisionalSection", "kiwisaverRow",
    "taxBreakdown",
    "liveRegion",
    "introContent",
    "studentLoanPayment", "studentLoanRow"
  ]

  connect() {
    this._debounce = new SmartDebounce(() => this._performCalculation(), { minDigits: 2 })
    this._lastParams = null
    this._showRateFields()
    this._prefillFromUrl()
  }

  disconnect() {
    this._debounce.cancel()
  }

  inputChanged(event) {
    this._debounce.schedule(detectEventType(event), this.rateInputTarget.value)
  }

  rateTypeChanged() {
    this._showRateFields()
    this._debounce.schedule("toggle", this.rateInputTarget.value)
  }

  expenseChanged() {
    if (this.hasExpenseDisplayTarget && this.hasExpenseInputTarget) {
      this.expenseDisplayTarget.textContent = `${this.expenseInputTarget.value}%`
    }
    this._debounce.schedule("toggle", this.rateInputTarget.value)
  }

  optionChanged() {
    this._debounce.schedule("toggle", this.rateInputTarget.value)
  }

  async submitForm(event) {
    event.preventDefault()
    this._debounce.schedule("submit")
  }

  // --- Private ---

  _prefillFromUrl() {
    const p = readGenericFromUrl(URL_MAP, URL_TYPES)
    if (!p.rate) return
    this.rateInputTarget.value = p.rate
    if (p.rate_type && this.hasRateTypeSelectTarget) { this.rateTypeSelectTarget.value = p.rate_type; this._showRateFields() }
    if (p.working_weeks != null && this.hasWorkingWeeksInputTarget) this.workingWeeksInputTarget.value = p.working_weeks
    if (p.hours_per_week != null && this.hasHoursInputTarget) this.hoursInputTarget.value = p.hours_per_week
    if (p.days_per_week != null && this.hasDaysInputTarget) this.daysInputTarget.value = p.days_per_week
    if (p.gst_registered && this.hasGstCheckboxTarget) this.gstCheckboxTarget.checked = true
    if (p.expense_percent != null && this.hasExpenseInputTarget) this.expenseInputTarget.value = p.expense_percent
    if (p.kiwisaver_perc != null && this.hasKiwisaverSelectTarget) this.kiwisaverSelectTarget.value = p.kiwisaver_perc
    if (p.include_student_loan && this.hasStudentLoanCheckboxTarget) this.studentLoanCheckboxTarget.checked = true
    if (this.element.dataset.prerendered !== "true") {
      this._performCalculation()
    } else {
      this._dispatchPrerenderedData()
    }
  }

  _showRateFields() {
    if (!this.hasRateTypeSelectTarget) return
    const type = this.rateTypeSelectTarget.value
    if (this.hasHoursFieldTarget) {
      this.hoursFieldTarget.classList.toggle("hidden", type === "daily" || type === "annual")
    }
    if (this.hasDaysFieldTarget) {
      this.daysFieldTarget.classList.toggle("hidden", type !== "daily")
    }
  }

  async _performCalculation() {
    const params = this._collectParams()
    this._enforceInputLimits(params)
    if (!params.rate || params.rate <= 0) return

    const paramsKey = JSON.stringify(params)
    if (paramsKey === this._lastParams) return
    this._lastParams = paramsKey

    this._showLoading(true)

    try {
      const result = await calculate(params)
      this._renderResult(result)
      writeGenericToUrl(params, URL_MAP)
      trackCalculatorUsed("contractor", { salaryAmount: result.gross_income, payFrequency: params.rate_type })
    } catch (error) {
      if (this.hasLiveRegionTarget) {
        this.liveRegionTarget.textContent = error.message
      }
    } finally {
      this._showLoading(false)
    }
  }

  _collectParams() {
    return {
      type: "contractor",
      rate: parseNumber(this.hasRateInputTarget ? this.rateInputTarget.value : "0"),
      rate_type: this.hasRateTypeSelectTarget ? this.rateTypeSelectTarget.value : "hourly",
      working_weeks: Math.min(52, Math.max(1, this.hasWorkingWeeksInputTarget ? parseInt(this.workingWeeksInputTarget.value) || this.defaultWeeksValue : this.defaultWeeksValue)),
      hours_per_week: Math.min(100, Math.max(1, this.hasHoursInputTarget ? parseFloat(this.hoursInputTarget.value) || this.defaultHoursValue : this.defaultHoursValue)),
      days_per_week: Math.min(7, Math.max(1, this.hasDaysInputTarget ? parseInt(this.daysInputTarget.value) || this.defaultDaysValue : this.defaultDaysValue)),
      gst_registered: this.hasGstCheckboxTarget ? this.gstCheckboxTarget.checked : false,
      expense_percent: this.hasExpenseInputTarget ? parseInt(this.expenseInputTarget.value) || this.defaultExpensePercentValue : this.defaultExpensePercentValue,
      kiwisaver_perc: this.hasKiwisaverSelectTarget ? parseFloat(this.kiwisaverSelectTarget.value) || 0 : 0,
      include_student_loan: this.hasStudentLoanCheckboxTarget ? this.studentLoanCheckboxTarget.checked : false,
    }
  }

  _enforceInputLimits(params) {
    if (this.hasWorkingWeeksInputTarget) this.workingWeeksInputTarget.value = params.working_weeks
    if (this.hasHoursInputTarget) this.hoursInputTarget.value = params.hours_per_week
    if (this.hasDaysInputTarget) this.daysInputTarget.value = params.days_per_week
  }

  _showLoading(show) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle("hidden", !show)
    }
  }

  _renderResult(result) {
    if (this.hasResultsPanelTarget) {
      this.resultsPanelTarget.classList.remove("hidden")
    }
    if (this.hasIntroContentTarget) {
      this.introContentTarget.classList.add("hidden")
    }

    this._setText("netTakeHome", formatCurrency(result.net_take_home))
    this._setText("effectiveHourly", formatCurrency(result.effective_hourly_rate, true))
    this._setText("effectiveDailyRate", formatCurrency(result.effective_daily_rate, true))
    this._setText("grossIncome", formatCurrency(result.gross_income))
    this._setText("businessExpenses", formatCurrency(result.business_expenses))
    this._setText("taxableIncome", formatCurrency(result.taxable_income))
    this._setText("payeTax", formatCurrency(result.paye_tax))
    this._setText("accLevy", formatCurrency(result.acc_levy))
    this._setText("equivalentPaye", formatCurrency(result.equivalent_paye_salary))
    this._setText("noLeaveCost", formatCurrency(result.no_leave_cost))
    this._setText("provisionalTax", formatCurrency(result.provisional_tax_per_instalment))

    // GST section
    if (this.hasGstSectionTarget) {
      this.gstSectionTarget.classList.toggle("hidden", !result.gst_registered)
    }
    this._setText("gstAmount", formatCurrency(result.gst_amount))
    this._setText("gstInclusiveIncome", formatCurrency(result.gst_inclusive_income))
    this._setText("gstQuarterly", formatCurrency(result.gst_per_quarter))

    // KiwiSaver row
    if (this.hasKiwisaverRowTarget) {
      this.kiwisaverRowTarget.classList.toggle("hidden", result.kiwisaver_voluntary <= 0)
    }
    this._setText("kiwisaverVoluntary", formatCurrency(result.kiwisaver_voluntary))

    // Student loan row
    if (this.hasStudentLoanRowTarget) {
      this.studentLoanRowTarget.classList.toggle("hidden", !result.student_loan || result.student_loan <= 0)
    }
    this._setText("studentLoanPayment", formatCurrency(result.student_loan))

    // Frequency table
    const freq = result.frequencies || {}
    this._setFrequency("annual", freq.annual)
    this._setFrequency("monthly", freq.monthly)
    this._setFrequency("fortnightly", freq.fortnightly)
    this._setFrequency("weekly", freq.weekly)

    // Tax breakdown
    this._renderTaxBreakdown(result.tax_breakdown)

    // Dispatch for chart
    this.dispatch("calculated", { detail: result })
  }

  _setFrequency(name, data) {
    if (!data) return
    this._setText(`${name}Net`, formatCurrency(data.net))
  }

  _renderTaxBreakdown(breakdown) {
    if (!this.hasTaxBreakdownTarget || !breakdown) return

    const rows = breakdown.map(bracket => {
      const rate = bracket.rate != null ? `${bracket.rate}%` : bracket.description
      return `<tr>
        <td class="py-1 text-sm text-gray-600">${rate}</td>
        <td class="py-1 text-sm tabular-nums text-right">${formatCurrency(bracket.attributable_income)}</td>
        <td class="py-1 text-sm tabular-nums text-right font-medium">${formatCurrency(bracket.tax_component)}</td>
      </tr>`
    }).join("")

    this.taxBreakdownTarget.innerHTML = rows
  }

  _setText(targetName, text) {
    const capitalized = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    if (this[`has${capitalized}Target`]) {
      this[`${targetName}Target`].textContent = text
    }
  }

  _announceResult(result) {
    if (!this.hasLiveRegionTarget) return
    this.liveRegionTarget.textContent =
      `Your contractor take-home pay is ${formatCurrency(result.net_take_home)} per year, ` +
      `effective hourly rate ${formatCurrency(result.effective_hourly_rate, true)}.`
  }

  _dispatchPrerenderedData() {
    const json = this.element.dataset.prerenderedResults
    if (!json) return
    try {
      const result = JSON.parse(json)
      requestAnimationFrame(() => this._renderResult(result))
    } catch { /* ignore parse errors */ }
  }
}
